import type {
  AgentProgress,
  UIBlock,
  ScopeBlock,
  ToolCallBlock,
  MessageBlock,
  ErrorBlock,
  ThinkingBlock,
  ThoughtStreamBlock,
  TaskBlock,
} from '@trinity/chat';
import type {
  TimelineNode,
  ScopeNode,
  ToolCallNode,
  MessageNode,
  ErrorNode,
  ReasoningNode,
  TaskNode,
  CardPhase,
  CardState,
  ParallelGroupNode,
  GroupedToolCallsNode,
} from './interfaces';

function isScopeBlock(block: UIBlock): block is ScopeBlock {
  return block.kind === 'scope';
}

function isToolCallBlock(block: UIBlock): block is ToolCallBlock {
  return block.kind === 'tool_call';
}

function isMessageBlock(block: UIBlock): block is MessageBlock {
  return block.kind === 'message';
}

function isErrorBlock(block: UIBlock): block is ErrorBlock {
  return block.kind === 'error';
}

function isThinkingBlock(block: UIBlock): block is ThinkingBlock {
  return block.kind === 'thinking';
}

function isThoughtStreamBlock(block: UIBlock): block is ThoughtStreamBlock {
  return block.kind === 'thought_stream';
}

function isTaskBlock(block: UIBlock): block is TaskBlock {
  return block.kind === 'task';
}

export function getTimelineNodeKey(node: TimelineNode): string {
  switch (node.kind) {
    case 'reasoning':
      return node.id;
    case 'parallel_group':
      return `pg_${node.children.map(getTimelineNodeKey).join('_')}`;
    case 'grouped_tool_calls':
      return `gtc_${node.firstChildId}`;
    case 'scope':
    case 'tool_call':
    case 'message':
    case 'error':
    case 'task':
      return node.block.id;
    default:
      return '';
  }
}

function shouldSkipAsStandalone(state: AgentProgress, block: UIBlock): boolean {
  if (block.kind !== 'thought_stream') return false;

  const parent = block.parentBlockId ? state.blocks[block.parentBlockId] : undefined;
  return !!parent && parent.kind === 'thinking';
}

function buildReasoningNode(state: AgentProgress, thinkingBlock: ThinkingBlock): ReasoningNode {
  const thoughtChild = thinkingBlock.childBlockIds
    .map((id) => state.blocks[id])
    .find((child): child is ThoughtStreamBlock => !!child && isThoughtStreamBlock(child));

  const status = thoughtChild?.status ?? thinkingBlock.status;
  const durationMs = status === 'completed' ? thinkingBlock.updatedAt - thinkingBlock.createdAt : undefined;

  return {
    kind: 'reasoning',
    id: thinkingBlock.id,
    status,
    summary: thinkingBlock.summary,
    detailText: thoughtChild?.text,
    streaming: thoughtChild?.streaming ?? thinkingBlock.status === 'running',
    depth: thinkingBlock.depth,
    durationMs,
  };
}

export function buildTimelineNode(state: AgentProgress, blockId: string): TimelineNode | null {
  const block = state.blocks[blockId];
  if (!block) return null;

  if (shouldSkipAsStandalone(state, block)) {
    return null;
  }

  if (isThinkingBlock(block)) {
    return buildReasoningNode(state, block);
  }

  if (isScopeBlock(block)) {
    const children = block.childBlockIds
      .map((childId) => buildTimelineNode(state, childId))
      .filter((node): node is TimelineNode => node !== null);

    const node: ScopeNode = {
      kind: 'scope',
      block,
      children,
    };

    return node;
  }

  if (isToolCallBlock(block)) {
    const children = block.childBlockIds
      .map((childId) => buildTimelineNode(state, childId))
      .filter((node): node is TimelineNode => node !== null);

    const node: ToolCallNode = {
      kind: 'tool_call',
      block,
      children,
    };

    return node;
  }

  if (isMessageBlock(block)) {
    const node: MessageNode = {
      kind: 'message',
      block,
    };

    return node;
  }

  if (isErrorBlock(block)) {
    const node: ErrorNode = {
      kind: 'error',
      block,
    };

    return node;
  }

  if (isTaskBlock(block)) {
    const children = block.childBlockIds
      .map((childId) => buildTimelineNode(state, childId))
      .filter((node): node is TimelineNode => node !== null);

    const node: TaskNode = {
      kind: 'task',
      block,
      children,
    };

    return node;
  }

  // standalone thought_stream fallback if not parented to thinking
  if (block.kind === 'thought_stream') {
    return {
      kind: 'reasoning',
      id: block.id,
      status: block.status,
      summary: 'Reasoning',
      detailText: block.text,
      streaming: block.streaming,
      depth: block.depth,
    };
  }

  return null;
}

/**
 * Returns the effective createdAt for ordering a root-level timeline node.
 * Depth-0 scope nodes are transparent containers whose visible content
 * (e.g. a response message) may be appended long after the scope itself was
 * created.  Using the first child's createdAt ensures those nodes sort into
 * the correct chronological position relative to sibling task/tool nodes.
 */
function getEffectiveCreatedAt(node: TimelineNode): number {
  if (node.kind === 'scope' && node.block.depth === 0 && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild !== undefined) {
      return getEffectiveCreatedAt(firstChild);
    }
  }
  if (node.kind === 'reasoning') {
    return 0; // reasoning nodes have no block – keep their original position
  }
  if (node.kind === 'parallel_group') {
    return 0;
  }
  if (node.kind === 'grouped_tool_calls') {
    return 0;
  }
  return node.block.createdAt;
}

export function buildTimeline(state: AgentProgress): TimelineNode[] {
  const nodes = state.rootTimeline
    .map((blockId) => buildTimelineNode(state, blockId))
    .filter((node): node is TimelineNode => node !== null);

  // Sort by effective createdAt so that transparent depth-0 scope nodes
  // (whose message children may arrive after sibling tasks complete) appear
  // in the correct chronological position in the timeline.
  return nodes.slice().sort((a, b) => getEffectiveCreatedAt(a) - getEffectiveCreatedAt(b));
}

const PARALLEL_THRESHOLD_MS = 500;

/** Flatten scope nodes so their children render directly in the parent list. */
export function flattenNodes(nodes: TimelineNode[]): TimelineNode[] {
  const result: TimelineNode[] = [];
  for (const node of nodes) {
    if (node.kind === 'scope') {
      result.push(...flattenNodes(node.children));
    } else {
      result.push(node);
    }
  }
  return result;
}

/** Flatten the entire timeline tree for aggregate status/count calculations. */
export function flattenTimelineTree(nodes: TimelineNode[]): TimelineNode[] {
  const result: TimelineNode[] = [];

  for (const node of nodes) {
    result.push(node);

    if (node.kind === 'scope' || node.kind === 'tool_call' || node.kind === 'task') {
      result.push(...flattenTimelineTree(node.children));
      continue;
    }

    if (node.kind === 'parallel_group') {
      result.push(...flattenTimelineTree(node.children));
    }
  }

  return result;
}

/** Attach the preceding reasoning's detail text to each tool call node. */
export function attachReasoningToToolCalls(nodes: TimelineNode[]): TimelineNode[] {
  let lastReasoningText: string | undefined;

  return nodes.map((node) => {
    if (node.kind === 'reasoning') {
      lastReasoningText = node.detailText;
      return node;
    }

    if (node.kind === 'tool_call' && lastReasoningText) {
      return { ...node, reasoningText: lastReasoningText };
    }

    // Non-reasoning, non-tool-call node resets the trailing reasoning
    if (node.kind !== 'tool_call') {
      lastReasoningText = undefined;
    }

    return node;
  });
}

export function groupParallelNodes(nodes: TimelineNode[]): TimelineNode[] {
  const result: TimelineNode[] = [];
  let i = 0;

  while (i < nodes.length) {
    const current = nodes[i];
    if (!current) {
      i++;
      continue;
    }

    if (current.kind !== 'tool_call' || current.block.startedAt == null) {
      result.push(current);
      i++;
      continue;
    }

    const currentStartedAt = current.block.startedAt;
    const group: ToolCallNode[] = [current];
    let j = i + 1;

    while (j < nodes.length) {
      const next = nodes[j];
      if (
        next?.kind !== 'tool_call' ||
        next.block.startedAt == null ||
        Math.abs(next.block.startedAt - currentStartedAt) > PARALLEL_THRESHOLD_MS
      ) {
        break;
      }
      group.push(next);
      j++;
    }

    if (group.length > 1) {
      const parallelGroup: ParallelGroupNode = {
        kind: 'parallel_group',
        children: group,
      };
      result.push(parallelGroup);
    } else {
      result.push(current);
    }

    i = j;
  }

  return result;
}

/** Derives the rolled-up status for a group of parallel tool call nodes. */
function deriveGroupedStatus(nodes: ToolCallNode[]): GroupedToolCallsNode['status'] {
  if (nodes.some((n) => n.block.status === 'running')) return 'running';
  if (nodes.every((n) => n.block.status === 'completed')) return 'completed';
  if (nodes.some((n) => n.block.status === 'cancelled')) return 'cancelled';
  return 'pending';
}

/** Collapses a homogeneous parallel group (all children the same tool name) into a single GroupedToolCallsNode. Mixed-name groups are left unchanged. */
export function collapseRepeatedToolCalls(nodes: TimelineNode[]): TimelineNode[] {
  return nodes.map((node) => {
    if (node.kind !== 'parallel_group') return node;

    const toolChildren = node.children.filter((c): c is ToolCallNode => c.kind === 'tool_call');
    const firstName = toolChildren[0]?.block.name;
    const allSameName =
      !!firstName &&
      toolChildren.length === node.children.length &&
      toolChildren.length >= 2 &&
      toolChildren.every((c) => c.block.name === firstName);

    if (!allSameName) return node;

    return {
      kind: 'grouped_tool_calls' as const,
      toolName: firstName,
      count: toolChildren.length,
      status: deriveGroupedStatus(toolChildren),
      firstChildId: toolChildren[0]?.block.id ?? firstName,
    };
  });
}

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

function derivePhase(
  state: AgentProgress,
  toolNodes: ToolCallNode[],
  taskNodes: TaskNode[],
  groupedNodes: GroupedToolCallsNode[]
): CardPhase {
  if (state.status === 'cancelled') return 'cancelled';
  if (state.status === 'done' || state.status === 'error') return 'complete';
  if (
    toolNodes.some((n) => n.block.status === 'running' || n.block.status === 'completed') ||
    taskNodes.some((n) => n.block.status === 'running' || n.block.status === 'completed') ||
    groupedNodes.some((n) => n.status === 'running' || n.status === 'completed')
  ) {
    return 'executing';
  }
  return 'thinking';
}

function deriveHeaderForThinking(reasoningSummary: string | undefined): {
  headerLabel: string;
  headerSubtitle: string | undefined;
} {
  return { headerLabel: 'Thinking...', headerSubtitle: reasoningSummary };
}

function deriveHeaderForExecuting(
  isCurrentlyReasoning: boolean,
  stepsLabel: string | undefined,
  reasoningSummary: string | undefined
): { headerLabel: string; headerSubtitle: string | undefined } {
  const headerLabel = isCurrentlyReasoning ? 'Thinking...' : 'Executing...';
  const headerSubtitle = [stepsLabel, reasoningSummary].filter(Boolean).join(' \u00B7 ') || undefined;
  return { headerLabel, headerSubtitle };
}

function deriveHeaderForComplete(
  reasoningNodes: ReasoningNode[],
  totalSteps: number,
  stepsLabel: string | undefined
): { headerLabel: string; headerSubtitle: string | undefined } {
  let totalMs = 0;
  for (const n of reasoningNodes) {
    if (n.durationMs !== undefined) totalMs += n.durationMs;
  }
  if (totalSteps > 0) return { headerLabel: 'Execution Complete', headerSubtitle: stepsLabel };
  if (totalMs > 0)
    return { headerLabel: `Thought for ${formatDuration(totalMs)}`, headerSubtitle: undefined };
  return { headerLabel: 'Thought process', headerSubtitle: undefined };
}

export function deriveCardState(state: AgentProgress, flatNodes: TimelineNode[]): CardState {
  const toolNodes = flatNodes.filter((n): n is ToolCallNode => n.kind === 'tool_call');
  const taskNodes = flatNodes.filter((n): n is TaskNode => n.kind === 'task');
  const groupedNodes = flatNodes.filter((n): n is GroupedToolCallsNode => n.kind === 'grouped_tool_calls');

  // Exclude child task nodes whose label duplicates their parent task's label —
  // they are implementation details, not user-visible steps.
  const visibleTaskNodes = taskNodes.filter((n) => {
    const parentId = n.block.parentBlockId;
    if (!parentId) return true;
    const parentBlock = state.blocks[parentId];
    if (!parentBlock || parentBlock.kind !== 'task') return true;
    const parentLastStep = parentBlock.steps[parentBlock.steps.length - 1];
    const childLastStep = n.block.steps[n.block.steps.length - 1];
    return parentLastStep?.displayValue !== childLastStep?.displayValue;
  });

  const totalSteps = toolNodes.length + visibleTaskNodes.length + groupedNodes.length;
  const completedSteps =
    toolNodes.filter((n) => n.block.status === 'completed').length +
    visibleTaskNodes.filter((n) => n.block.status === 'completed').length +
    groupedNodes.filter((n) => n.status === 'completed').length;

  const phase = derivePhase(state, toolNodes, taskNodes, groupedNodes);

  // Derive reasoning summary from the latest reasoning node
  const reasoningNodes = flatNodes.filter((n): n is ReasoningNode => n.kind === 'reasoning');
  const latestReasoning = reasoningNodes[reasoningNodes.length - 1];
  const isCurrentlyReasoning =
    latestReasoning !== undefined && (latestReasoning.streaming || latestReasoning.status === 'running');
  const reasoningSummary =
    latestReasoning?.summary && latestReasoning.summary !== 'Thinking...'
      ? latestReasoning.summary
      : undefined;

  const stepsLabel = totalSteps > 0 ? `${completedSteps} / ${totalSteps} steps` : undefined;

  let headerLabel!: string;
  let headerSubtitle: string | undefined;

  switch (phase) {
    case 'thinking': {
      const h = deriveHeaderForThinking(reasoningSummary);
      headerLabel = h.headerLabel;
      headerSubtitle = h.headerSubtitle;
      break;
    }
    case 'executing': {
      const h = deriveHeaderForExecuting(isCurrentlyReasoning, stepsLabel, reasoningSummary);
      headerLabel = h.headerLabel;
      headerSubtitle = h.headerSubtitle;
      break;
    }
    case 'complete': {
      const h = deriveHeaderForComplete(reasoningNodes, totalSteps, stepsLabel);
      headerLabel = h.headerLabel;
      headerSubtitle = h.headerSubtitle;
      break;
    }
    case 'cancelled': {
      headerLabel = 'Paused by user';
      headerSubtitle = stepsLabel;
      break;
    }
    default:
      break;
  }

  return {
    phase,
    headerLabel,
    headerSubtitle,
    completedSteps,
    totalSteps,
    summaryMetadata: state.summaryMetadata,
    cancelledAt: state.cancelledAt,
  };
}
